import type { MouseEvent } from 'react'
import { flushSync } from 'react-dom'
import { Monitor, Moon, Sun } from 'lucide-react'
import { useTheme, type Theme } from '@/components/ThemeProvider'
import { cn } from '@/lib/cn'

const OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light theme', icon: Sun },
  { value: 'dark', label: 'Dark theme', icon: Moon },
  { value: 'system', label: 'Match system theme', icon: Monitor },
]

type TransitionDocument = Document & {
  startViewTransition?: (update: () => void) => { ready: Promise<void>; finished: Promise<void> }
}

/**
 * The new theme opens as a circle from the button that chose it, over the old one, where the
 * browser has view transitions and motion is welcome; elsewhere it simply switches.
 */
function reveal(event: MouseEvent<HTMLButtonElement>, change: () => void) {
  const doc = document as TransitionDocument
  if (!doc.startViewTransition || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    change()
    return
  }
  const r = event.currentTarget.getBoundingClientRect()
  const x = r.left + r.width / 2
  const y = r.top + r.height / 2
  const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y))
  const root = document.documentElement
  root.classList.add('gd-theme-reveal')
  const transition = doc.startViewTransition(() => flushSync(change))
  transition.ready
    .then(() => {
      root.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
        {
          duration: 560,
          easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
          pseudoElement: '::view-transition-new(root)',
        },
      )
    })
    .catch(() => {})
  transition.finished.finally(() => root.classList.remove('gd-theme-reveal')).catch(() => {})
}

/** Cycles light -> dark -> system on click; each button is directly selectable too. */
export function ThemeToggle() {
  const { theme, setTheme } = useTheme()

  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-line bg-surface-2 p-0.5">
      {OPTIONS.map((option) => {
        const active = option.value === theme
        return (
          <button
            key={option.value}
            type="button"
            aria-label={option.label}
            aria-pressed={active}
            onClick={(e) => {
              if (option.value !== theme) reveal(e, () => setTheme(option.value))
            }}
            className={cn(
              'inline-flex size-7 items-center justify-center rounded-sm transition-colors duration-150 pointer-coarse:size-9',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
              active ? 'bg-surface-pop text-ink shadow-thumb' : 'text-ink-2 hover:text-ink',
            )}
          >
            <option.icon className="size-3.5" aria-hidden="true" />
          </button>
        )
      })}
    </div>
  )
}
